import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectApi, taskApi, columnApi, userApi, flagsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft,
  Loader2,
  LayoutGrid,
  List,
  Search,
  Filter,
  Plus,
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  ChevronRight,
  User,
  Calendar,
  Flag,
  PlayCircle,
  Eye,
  RotateCcw,
  MoreVertical,
  Trash2,
  Pencil,
  LayoutList,
  Grid3X3,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'cards'
  
  // Task dialog
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    column_id: '',
    priority: 'medium',
    due_date: '',
    assigned_users: [],
  });

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectRes, tasksRes, columnsRes, usersRes] = await Promise.all([
        projectApi.get(projectId),
        taskApi.list(projectId),
        columnApi.list(projectId),
        userApi.list(),
      ]);
      
      setProject(projectRes.data);
      setTasks(tasksRes.data);
      setColumns(columnsRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados do projeto');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTaskFlag = async (taskId, flag, e) => {
    if (e) e.stopPropagation();
    try {
      await flagsApi.updateTaskFlag(taskId, flag);
      const flagLabels = {
        'proxima_acao': 'Próxima Ação',
        'aguardando_retorno': 'Aguardando Retorno',
        'revisar': 'Revisar'
      };
      toast.success(flag ? `Flag definida: ${flagLabels[flag]}` : 'Flag removida');
      loadData();
    } catch (error) {
      console.error('Error updating flag:', error);
      toast.error('Erro ao atualizar flag');
    }
  };

  const handleToggleComplete = async (task, e) => {
    if (e) e.stopPropagation();
    try {
      const newStatus = task.completed_date ? null : new Date().toISOString();
      await taskApi.update(task.id, { completed_date: newStatus });
      toast.success(newStatus ? 'Tarefa concluída!' : 'Tarefa reaberta');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar tarefa');
    }
  };

  const handleDeleteTask = async (taskId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja excluir esta tarefa?')) return;
    
    try {
      await taskApi.delete(taskId);
      toast.success('Tarefa excluída');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir tarefa');
    }
  };

  const openNewTaskDialog = () => {
    setSelectedTask(null);
    setTaskForm({
      title: '',
      description: '',
      column_id: columns[0]?.id || '',
      priority: 'medium',
      due_date: '',
      assigned_users: [],
    });
    setTaskDialogOpen(true);
  };

  const openEditTaskDialog = (task, e) => {
    if (e) e.stopPropagation();
    setSelectedTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      column_id: task.column_id,
      priority: task.priority || 'medium',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      assigned_users: task.assigned_users || [],
    });
    setTaskDialogOpen(true);
  };

  const handleSaveTask = async () => {
    if (!taskForm.title.trim()) {
      toast.error('Informe o título da tarefa');
      return;
    }

    try {
      if (selectedTask) {
        await taskApi.update(selectedTask.id, taskForm);
        toast.success('Tarefa atualizada');
      } else {
        await taskApi.create({ ...taskForm, project_id: projectId });
        toast.success('Tarefa criada');
      }
      setTaskDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar tarefa');
    }
  };

  const getFlagInfo = (flag) => {
    const flags = {
      'proxima_acao': { label: 'Próxima Ação', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: PlayCircle },
      'aguardando_retorno': { label: 'Aguardando', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock },
      'revisar': { label: 'Revisar', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Eye },
    };
    return flags[flag] || null;
  };

  const getStatusInfo = (task) => {
    if (task.completed_date) {
      return { label: 'Concluída', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle2 };
    }
    const column = columns.find(c => c.id === task.column_id);
    return { label: column?.name || 'A Fazer', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', icon: Circle };
  };

  const getPriorityInfo = (priority) => {
    const priorities = {
      'urgent': { label: 'Urgente', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
      'high': { label: 'Alta', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
      'medium': { label: 'Média', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      'low': { label: 'Baixa', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
    };
    return priorities[priority] || priorities['medium'];
  };

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'completed' && task.completed_date) ||
      (filterStatus === 'pending' && !task.completed_date);
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Stats
  const completedCount = tasks.filter(t => t.completed_date).length;
  const progressPercent = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Projeto não encontrado</p>
        <Button variant="ghost" onClick={() => navigate('/projects')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para Projetos
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/projects')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{project.name}</h1>
            {project.description && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{project.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate(`/projects/${projectId}/kanban`)}
            className="gap-2"
          >
            <LayoutGrid className="w-4 h-4" />
            Ver Kanban
          </Button>
          <Button onClick={openNewTaskDialog} className="gap-2">
            <Plus className="w-4 h-4" />
            Nova Tarefa
          </Button>
        </div>
      </div>

      {/* Progress Card */}
      <Card className="dark:bg-slate-800/50 dark:border-slate-700">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Progresso do Projeto
            </span>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {completedCount} de {tasks.length} tarefas
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{progressPercent}% concluído</p>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar tarefas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendentes</SelectItem>
            <SelectItem value="completed">Concluídas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Prioridade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="urgent">Urgente</SelectItem>
            <SelectItem value="high">Alta</SelectItem>
            <SelectItem value="medium">Média</SelectItem>
            <SelectItem value="low">Baixa</SelectItem>
          </SelectContent>
        </Select>
        {/* View Mode Toggle */}
        <div className="flex border rounded-md dark:border-slate-700">
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-r-none px-3"
            onClick={() => setViewMode('list')}
          >
            <LayoutList className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'cards' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-l-none px-3"
            onClick={() => setViewMode('cards')}
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tasks - List or Cards View */}
      {viewMode === 'list' ? (
        /* Table View */
        <Card className="dark:bg-slate-800/50 dark:border-slate-700">
          <CardContent className="p-0">
            {filteredTasks.length === 0 ? (
              <div className="text-center py-12">
                <List className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                <p className="text-slate-500 dark:text-slate-400">
                  {tasks.length === 0 ? 'Nenhuma tarefa neste projeto' : 'Nenhuma tarefa encontrada'}
                </p>
                {tasks.length === 0 && (
                  <Button variant="outline" onClick={openNewTaskDialog} className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                  Criar primeira tarefa
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="dark:border-slate-700">
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="dark:text-slate-300">Tarefa</TableHead>
                  <TableHead className="dark:text-slate-300">Coluna</TableHead>
                  <TableHead className="dark:text-slate-300">Prioridade</TableHead>
                  <TableHead className="dark:text-slate-300">Flag</TableHead>
                  <TableHead className="dark:text-slate-300">Prazo</TableHead>
                  <TableHead className="dark:text-slate-300">Responsáveis</TableHead>
                  <TableHead className="dark:text-slate-300 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTasks.map((task) => {
                  const statusInfo = getStatusInfo(task);
                  const priorityInfo = getPriorityInfo(task.priority);
                  const flagInfo = getFlagInfo(task.flag);
                  const FlagIcon = flagInfo?.icon;
                  
                  return (
                    <TableRow 
                      key={task.id} 
                      className="dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/80"
                      onClick={() => openEditTaskDialog(task)}
                    >
                      <TableCell>
                        <Checkbox
                          checked={!!task.completed_date}
                          onCheckedChange={() => handleToggleComplete(task)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="min-w-0">
                          <p className={`font-medium text-slate-900 dark:text-slate-100 truncate ${task.completed_date ? 'line-through text-slate-400 dark:text-slate-500' : ''}`}>
                            {task.title}
                          </p>
                          {task.description && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-xs">
                              {task.description}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${statusInfo.color}`}>
                          {statusInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${priorityInfo.color}`}>
                          {priorityInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={`h-6 w-6 ${task.flag ? 'text-amber-500' : 'text-slate-400 hover:text-slate-600'}`}
                              >
                                <Flag className="w-3 h-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-44 dark:bg-slate-800 dark:border-slate-700">
                              <DropdownMenuItem 
                                onClick={(e) => handleUpdateTaskFlag(task.id, 'proxima_acao', e)}
                                className={`gap-2 text-sm dark:text-slate-200 ${task.flag === 'proxima_acao' ? 'bg-green-50 dark:bg-green-900/30' : ''}`}
                              >
                                <PlayCircle className="w-3.5 h-3.5 text-green-600" />
                                Próxima Ação
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={(e) => handleUpdateTaskFlag(task.id, 'aguardando_retorno', e)}
                                className={`gap-2 text-sm dark:text-slate-200 ${task.flag === 'aguardando_retorno' ? 'bg-amber-50 dark:bg-amber-900/30' : ''}`}
                              >
                                <Clock className="w-3.5 h-3.5 text-amber-600" />
                                Aguardando
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={(e) => handleUpdateTaskFlag(task.id, 'revisar', e)}
                                className={`gap-2 text-sm dark:text-slate-200 ${task.flag === 'revisar' ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                              >
                                <Eye className="w-3.5 h-3.5 text-blue-600" />
                                Revisar
                              </DropdownMenuItem>
                              {task.flag && (
                                <>
                                  <DropdownMenuSeparator className="dark:bg-slate-700" />
                                  <DropdownMenuItem 
                                    onClick={(e) => handleUpdateTaskFlag(task.id, null, e)}
                                    className="gap-2 text-sm text-slate-500 dark:text-slate-400"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                    Remover
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          {task.flag && flagInfo && FlagIcon && (
                            <Badge className={`text-[10px] px-1 py-0 ${flagInfo.color}`}>
                              <FlagIcon className="w-2.5 h-2.5" />
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {task.due_date ? (
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {format(new Date(task.due_date), 'dd/MM/yyyy')}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex -space-x-1">
                          {(task.assigned_users || []).slice(0, 3).map((userId) => {
                            const u = users.find(u => u.id === userId);
                            return u ? (
                              <div 
                                key={userId}
                                className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-[10px] font-medium border-2 border-white dark:border-slate-800"
                                title={u.name}
                              >
                                {u.name?.charAt(0).toUpperCase()}
                              </div>
                            ) : null;
                          })}
                          {(task.assigned_users || []).length > 3 && (
                            <div className="w-6 h-6 rounded-full bg-slate-300 dark:bg-slate-500 flex items-center justify-center text-[10px] font-medium border-2 border-white dark:border-slate-800">
                              +{task.assigned_users.length - 3}
                            </div>
                          )}
                          {(!task.assigned_users || task.assigned_users.length === 0) && (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="dark:bg-slate-800 dark:border-slate-700">
                            <DropdownMenuItem 
                              onClick={(e) => openEditTaskDialog(task, e)}
                              className="gap-2 dark:text-slate-200"
                            >
                              <Pencil className="w-4 h-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="dark:bg-slate-700" />
                            <DropdownMenuItem 
                              onClick={(e) => handleDeleteTask(task.id, e)}
                              className="gap-2 text-red-600 dark:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      ) : (
        /* Cards View */
        <div>
          {filteredTasks.length === 0 ? (
            <Card className="dark:bg-slate-800/50 dark:border-slate-700">
              <CardContent className="py-12">
                <div className="text-center">
                  <Grid3X3 className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                  <p className="text-slate-500 dark:text-slate-400">
                    {tasks.length === 0 ? 'Nenhuma tarefa neste projeto' : 'Nenhuma tarefa encontrada'}
                  </p>
                  {tasks.length === 0 && (
                    <Button variant="outline" onClick={openNewTaskDialog} className="mt-4">
                      <Plus className="w-4 h-4 mr-2" />
                      Criar primeira tarefa
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTasks.map((task) => {
                const statusInfo = getStatusInfo(task);
                const priorityInfo = getPriorityInfo(task.priority);
                const flagInfo = getFlagInfo(task.flag);
                const FlagIcon = flagInfo?.icon;
                const StatusIcon = statusInfo.icon;
                
                return (
                  <Card 
                    key={task.id}
                    className={`dark:bg-slate-800/50 dark:border-slate-700 cursor-pointer hover:shadow-md transition-shadow ${task.completed_date ? 'opacity-60' : ''}`}
                    onClick={() => openEditTaskDialog(task)}
                  >
                    <CardContent className="p-4">
                      {/* Header with checkbox and actions */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          <Checkbox
                            checked={!!task.completed_date}
                            onCheckedChange={() => handleToggleComplete(task)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-0.5"
                          />
                          <div className="min-w-0">
                            <h3 className={`font-medium text-slate-900 dark:text-slate-100 line-clamp-2 ${task.completed_date ? 'line-through text-slate-400' : ''}`}>
                              {task.title}
                            </h3>
                            {task.description && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                                {task.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="dark:bg-slate-800 dark:border-slate-700">
                            <DropdownMenuItem 
                              onClick={(e) => openEditTaskDialog(task, e)}
                              className="gap-2 dark:text-slate-200"
                            >
                              <Pencil className="w-4 h-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="dark:bg-slate-700" />
                            <DropdownMenuItem 
                              onClick={(e) => handleDeleteTask(task.id, e)}
                              className="gap-2 text-red-600 dark:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      {/* Badges row */}
                      <div className="flex flex-wrap items-center gap-1.5 mb-3">
                        <Badge variant="outline" className={`text-[10px] ${statusInfo.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusInfo.label}
                        </Badge>
                        <Badge className={`text-[10px] ${priorityInfo.color}`}>
                          {priorityInfo.label}
                        </Badge>
                        {task.flag && flagInfo && FlagIcon && (
                          <Badge className={`text-[10px] ${flagInfo.color}`}>
                            <FlagIcon className="w-3 h-3 mr-1" />
                            {flagInfo.label}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Flag dropdown */}
                      <div className="flex items-center justify-between border-t dark:border-slate-700 pt-3 mt-auto">
                        <div className="flex items-center gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={`h-7 px-2 text-xs ${task.flag ? 'text-amber-500' : 'text-slate-400 hover:text-slate-600'}`}
                              >
                                <Flag className="w-3 h-3 mr-1" />
                                Flag
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-44 dark:bg-slate-800 dark:border-slate-700">
                              <DropdownMenuItem 
                                onClick={(e) => handleUpdateTaskFlag(task.id, 'proxima_acao', e)}
                                className={`gap-2 text-sm dark:text-slate-200 ${task.flag === 'proxima_acao' ? 'bg-green-50 dark:bg-green-900/30' : ''}`}
                              >
                                <PlayCircle className="w-3.5 h-3.5 text-green-600" />
                                Próxima Ação
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={(e) => handleUpdateTaskFlag(task.id, 'aguardando_retorno', e)}
                                className={`gap-2 text-sm dark:text-slate-200 ${task.flag === 'aguardando_retorno' ? 'bg-amber-50 dark:bg-amber-900/30' : ''}`}
                              >
                                <Clock className="w-3.5 h-3.5 text-amber-600" />
                                Aguardando
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={(e) => handleUpdateTaskFlag(task.id, 'revisar', e)}
                                className={`gap-2 text-sm dark:text-slate-200 ${task.flag === 'revisar' ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                              >
                                <Eye className="w-3.5 h-3.5 text-blue-600" />
                                Revisar
                              </DropdownMenuItem>
                              {task.flag && (
                                <>
                                  <DropdownMenuSeparator className="dark:bg-slate-700" />
                                  <DropdownMenuItem 
                                    onClick={(e) => handleUpdateTaskFlag(task.id, null, e)}
                                    className="gap-2 text-sm text-slate-500 dark:text-slate-400"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                    Remover
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        
                        {/* Due date and assignees */}
                        <div className="flex items-center gap-2">
                          {task.due_date && (
                            <span className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(task.due_date), 'dd/MM')}
                            </span>
                          )}
                          <div className="flex -space-x-1">
                            {(task.assigned_users || []).slice(0, 2).map((userId) => {
                              const u = users.find(u => u.id === userId);
                              return u ? (
                                <div 
                                  key={userId}
                                  className="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-[9px] font-medium border border-white dark:border-slate-800"
                                  title={u.name}
                                >
                                  {u.name?.charAt(0).toUpperCase()}
                                </div>
                              ) : null;
                            })}
                            {(task.assigned_users || []).length > 2 && (
                              <div className="w-5 h-5 rounded-full bg-slate-300 dark:bg-slate-500 flex items-center justify-center text-[9px] font-medium border border-white dark:border-slate-800">
                                +{task.assigned_users.length - 2}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="sm:max-w-lg dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">
              {selectedTask ? 'Editar Tarefa' : 'Nova Tarefa'}
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {selectedTask ? 'Atualize as informações da tarefa' : 'Preencha os dados da nova tarefa'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="dark:text-slate-200">Título *</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                placeholder="Digite o título da tarefa"
                className="dark:bg-slate-900 dark:border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="dark:text-slate-200">Descrição</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="Descreva a tarefa..."
                rows={3}
                className="dark:bg-slate-900 dark:border-slate-700"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="dark:text-slate-200">Coluna</Label>
                <Select
                  value={taskForm.column_id}
                  onValueChange={(val) => setTaskForm({ ...taskForm, column_id: val })}
                >
                  <SelectTrigger className="dark:bg-slate-900 dark:border-slate-700">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {columns.map((col) => (
                      <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="dark:text-slate-200">Prioridade</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(val) => setTaskForm({ ...taskForm, priority: val })}
                >
                  <SelectTrigger className="dark:bg-slate-900 dark:border-slate-700">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="dark:text-slate-200">Prazo</Label>
              <Input
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                className="dark:bg-slate-900 dark:border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="dark:text-slate-200">Responsáveis</Label>
              <div className="flex flex-wrap gap-2 p-2 border rounded-md dark:border-slate-700 min-h-[60px]">
                {users.map((u) => (
                  <Badge
                    key={u.id}
                    variant={taskForm.assigned_users.includes(u.id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      const newAssigned = taskForm.assigned_users.includes(u.id)
                        ? taskForm.assigned_users.filter(id => id !== u.id)
                        : [...taskForm.assigned_users, u.id];
                      setTaskForm({ ...taskForm, assigned_users: newAssigned });
                    }}
                  >
                    {u.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveTask}>
              {selectedTask ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
